import { create } from "zustand";
import {
  ContactUpcomingLocationTasks,
  fetchContactUpcomingTasksByLocation,
  fetchPortalLocations,
} from "../api";
import { PortalLocation } from "../types";
import { toLocalISODate } from "../../utils";

type LoadingState = { on?: boolean; message?: string };

type PortalState = {
  locations: PortalLocation[];
  error: string | null;
  loading: LoadingState;
  hydrated: boolean;
  upcomingTasksByLocationId: Record<string, ContactUpcomingLocationTasks>;
  upcomingLoadedLocationIds: Record<string, true>;
  upcomingReferenceDay: string | null;
  setLoading: (loading: LoadingState) => void;
  reset: () => void;
  loadLocations: () => Promise<void>;
  loadUpcomingTasks: (options?: {
    locationIds?: string[];
    onOrAfter?: string;
    force?: boolean;
  }) => Promise<void>;
};

const upcomingLoads = new Map<string, Promise<void>>();

export const usePortalStore = create<PortalState>((set, get) => ({
  locations: [],
  error: null,
  loading: { on: true },
  hydrated: false,
  upcomingTasksByLocationId: {},
  upcomingLoadedLocationIds: {},
  upcomingReferenceDay: null,
  setLoading: (loading) => set({ loading }),
  reset: () =>
    set({
      locations: [],
      error: null,
      loading: { on: true },
      hydrated: false,
      upcomingTasksByLocationId: {},
      upcomingLoadedLocationIds: {},
      upcomingReferenceDay: null,
    }),
  loadLocations: async () => {
    set({ loading: { on: true } });
    try {
      const data = await fetchPortalLocations();
      set({ locations: data, error: null, hydrated: true });
    } catch (e: any) {
      set({ error: e?.message || "Failed to load locations", hydrated: true });
    } finally {
      set({ loading: { on: false } });
    }
  },
  loadUpcomingTasks: async (options) => {
    const referenceDay = options?.onOrAfter || toLocalISODate(new Date());
    const requestedLocationIds = Array.from(
      new Set((options?.locationIds || []).filter(Boolean)),
    );
    const allLocationIds = get().locations
      .map((location) => location.id)
      .filter((id): id is string => Boolean(id));
    const effectiveLocationIds = requestedLocationIds.length
      ? requestedLocationIds
      : allLocationIds;
    const current = get();
    const sameDay = current.upcomingReferenceDay === referenceDay;
    if (
      !options?.force &&
      sameDay &&
      effectiveLocationIds.every(
        (locationId) => current.upcomingLoadedLocationIds[locationId],
      )
    ) {
      return;
    }
    const requestLocationIds = requestedLocationIds.length
      ? requestedLocationIds
      : [];
    const loadKey = `${referenceDay}:${requestLocationIds.slice().sort().join(",") || "*"}`;
    const existing = upcomingLoads.get(loadKey);
    if (existing) return existing;

    const request = (async () => {
      const summaries = await fetchContactUpcomingTasksByLocation({
        locationIds: requestLocationIds,
        onOrAfter: referenceDay,
      });
      set((state) => {
        const resetForNewDay = state.upcomingReferenceDay !== referenceDay;
        const summariesByLocationId = resetForNewDay
          ? {}
          : { ...state.upcomingTasksByLocationId };
        effectiveLocationIds.forEach((locationId) => {
          delete summariesByLocationId[locationId];
        });
        summaries.forEach((summary) => {
          if (summary.locationId) {
            summariesByLocationId[summary.locationId] = summary;
          }
        });
        const loadedLocationIds = resetForNewDay
          ? {}
          : { ...state.upcomingLoadedLocationIds };
        effectiveLocationIds.forEach((locationId) => {
          loadedLocationIds[locationId] = true;
        });
        return {
          upcomingTasksByLocationId: summariesByLocationId,
          upcomingLoadedLocationIds: loadedLocationIds,
          upcomingReferenceDay: referenceDay,
        };
      });
    })().finally(() => {
      upcomingLoads.delete(loadKey);
    });
    upcomingLoads.set(loadKey, request);
    return request;
  },
}));
